/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, ListTodo, FileCheck2, ClipboardList, Sparkles, Mail } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import PracticeManagerLink from "@/components/PracticeManagerLink";
import { PRIORITIES, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import EmailComposer from "@/components/EmailComposer";

export default function FirmTasks() {
    const { tenant } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [requestsById, setRequestsById] = useState({});
    const [status, setStatus] = useState("not_done");
    const [source, setSource] = useState("all"); // "all" | "welcome"
    const [loading, setLoading] = useState(true);
    const [welcomeTask, setWelcomeTask] = useState(null);

    const isWelcomeTask = (t) => (t?.title || "").startsWith("Welcome new client");

    async function load() {
        setLoading(true);
        try {
            const params = status === "not_done" ? {} : { status };
            const { data } = await api.get("/tasks/", { params });
            let filtered = status === "not_done" ? data.filter((t) => t.status !== "done") : data;
            if (source === "welcome") filtered = filtered.filter(isWelcomeTask);
            setTasks(filtered);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        api.get("/clients/").then((r) => setClients(r.data)).catch(() => {});
        api.get("/auth/users").then((r) => setUsers(r.data.filter((u) => u.role !== "client"))).catch(() => {});
        api.get("/requests/").then((r) => {
            const map = {};
            (r.data || []).forEach((req) => { map[req.id] = req; });
            setRequestsById(map);
        }).catch(() => {});
    }, []);
    useEffect(() => { load(); /* eslint-disable-line */ }, [status, source]);

    const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
    const userName = (id) => users.find((u) => u.id === id)?.name || "Unassigned";

    async function setTaskStatus(t, s) {
        try {
            await api.patch(`/tasks/${t.id}`, { status: s });
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div>
            <PageHeader
                eyebrow="Tasks"
                title="Task board"
                description="Internal tasks for the team — assigned, prioritised and tied to clients. Stop using sticky notes."
                actions={<NewTaskDialog clients={clients} users={users} onCreated={load} />}
                testId="tasks-header"
            />

            <div className="co-card co-card-pad flex flex-wrap gap-3 items-end">
                <div>
                    <Label className="text-[11px] text-[#5E636E]">Status</Label>
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="mt-1 min-w-[180px]" data-testid="tasks-filter-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="not_done">Not done</SelectItem>
                            <SelectItem value="todo">To do</SelectItem>
                            <SelectItem value="in_progress">In progress</SelectItem>
                            <SelectItem value="done">Done</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[11px] text-[#5E636E]">Source</Label>
                    <div className="mt-1 inline-flex rounded-md border border-[#E4E4E7] overflow-hidden" data-testid="tasks-filter-source">
                        <button
                            type="button"
                            onClick={() => setSource("all")}
                            className={`px-3 py-1.5 text-[13px] transition-colors ${source === "all" ? "bg-[#0F1115] text-white" : "bg-white text-[#5E636E] hover:text-[#0F1115]"}`}
                            data-testid="tasks-filter-source-all"
                        >
                            All tasks
                        </button>
                        <button
                            type="button"
                            onClick={() => setSource("welcome")}
                            className={`px-3 py-1.5 text-[13px] inline-flex items-center gap-1.5 transition-colors ${source === "welcome" ? "bg-[#0F1115] text-white" : "bg-white text-[#5E636E] hover:text-[#0F1115]"}`}
                            data-testid="tasks-filter-source-welcome"
                        >
                            <Sparkles className="h-3.5 w-3.5" /> Welcome only
                        </button>
                    </div>
                </div>
            </div>

            <div className="mt-6">
                {loading ? <div className="text-sm text-[#5E636E]">Loading…</div> : tasks.length === 0 ? (
                    <EmptyState icon={ListTodo} title="No tasks" description="Create a task or change the filter." testId="tasks-empty" />
                ) : (
                    <div className="co-card overflow-hidden">
                        <ul className="divide-y divide-[#E4E4E7]" data-testid="tasks-list">
                            {tasks.map((t) => (
                                <li key={t.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`task-row-${t.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {isWelcomeTask(t) && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-medium text-emerald-700" title="Auto-created when a new ClientOps enrolment completed" data-testid={`task-welcome-badge-${t.id}`}>
                                                    <Sparkles className="h-3 w-3" /> Welcome
                                                </span>
                                            )}
                                            {t.linked_submission_id && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-[10px] font-medium text-blue-700" title="Auto-created from a client document submission">
                                                    <FileCheck2 className="h-3 w-3" /> Review
                                                </span>
                                            )}
                                            {t.linked_request_id && !t.linked_submission_id && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 border border-zinc-200 px-2 py-0.5 text-[10px] font-medium text-zinc-700">
                                                    <ClipboardList className="h-3 w-3" /> Request
                                                </span>
                                            )}
                                            <div className="text-sm font-medium truncate">{t.title}</div>
                                            <StatusBadge status={t.status} />
                                            <PriorityPill p={t.priority} />
                                            {(() => {
                                                const linked = t.linked_request_id ? requestsById[t.linked_request_id] : null;
                                                if (linked && linked.external_source === "practice_manager") {
                                                    return (
                                                        <PracticeManagerLink
                                                            url={tenant?.practice_manager_url}
                                                            label={tenant?.practice_manager_label}
                                                            entityId={linked.external_id}
                                                            entityKind="job"
                                                            testId={`task-pm-link-${t.id}`}
                                                        />
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                        <div className="mt-1 text-xs text-[#5E636E]">
                                            {clientName(t.client_id)} · Due {formatDate(t.due_date)} · {userName(t.assigned_to_user_id)}
                                        </div>
                                        {t.description && <div className="text-xs text-[#0F1115] mt-1 line-clamp-2">{t.description}</div>}
                                    </div>
                                    <div className="flex gap-2">
                                        {isWelcomeTask(t) && t.enrolment_id && t.status !== "done" && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1"
                                                onClick={() => setWelcomeTask(t)}
                                                data-testid={`task-send-welcome-${t.id}`}
                                            >
                                                <Mail className="h-3.5 w-3.5" /> Send welcome
                                            </Button>
                                        )}
                                        {t.status !== "in_progress" && t.status !== "done" && (
                                            <Button variant="outline" size="sm" onClick={() => setTaskStatus(t, "in_progress")} data-testid={`task-start-${t.id}`}>Start</Button>
                                        )}
                                        {t.status !== "done" && (
                                            <Button size="sm" className="bg-[#171717] hover:bg-[#2E2E2E] text-white" onClick={() => setTaskStatus(t, "done")} data-testid={`task-done-${t.id}`}>Done</Button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <WelcomeEmailDialog
                task={welcomeTask}
                onClose={() => setWelcomeTask(null)}
                onSent={() => { setWelcomeTask(null); load(); }}
            />
        </div>
    );
}

function PriorityPill({ p }) {
    const cls = p === "urgent" ? "bg-rose-100 text-rose-700"
        : p === "high" ? "bg-amber-100 text-amber-700"
        : p === "low" ? "bg-zinc-100 text-zinc-600"
        : "bg-blue-50 text-blue-700";
    return <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${cls}`}>{p}</span>;
}

function NewTaskDialog({ clients, users, onCreated }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        title: "", description: "", client_id: "", assigned_to_user_id: "", due_date: "", priority: "medium",
    });
    const [busy, setBusy] = useState(false);
    const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function submit() {
        if (!form.title) return;
        setBusy(true);
        try {
            const payload = { ...form };
            ["client_id", "assigned_to_user_id", "due_date"].forEach((k) => { if (!payload[k]) delete payload[k]; });
            await api.post("/tasks/", payload);
            toast.success("Task created");
            setOpen(false);
            setForm({ title: "", description: "", client_id: "", assigned_to_user_id: "", due_date: "", priority: "medium" });
            onCreated?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="new-task-btn"><Plus className="h-4 w-4" /> New task</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="new-task-dialog">
                <DialogHeader><DialogTitle>Create task</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div><Label>Title</Label><Input className="mt-1" value={form.title} onChange={(e) => s("title", e.target.value)} data-testid="nt-title" /></div>
                    <div><Label>Description</Label><Textarea rows={2} className="mt-1" value={form.description} onChange={(e) => s("description", e.target.value)} data-testid="nt-description" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Client (optional)</Label>
                            <Select value={form.client_id} onValueChange={(v) => s("client_id", v)}>
                                <SelectTrigger className="mt-1" data-testid="nt-client"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Assigned to</Label>
                            <Select value={form.assigned_to_user_id} onValueChange={(v) => s("assigned_to_user_id", v)}>
                                <SelectTrigger className="mt-1" data-testid="nt-assigned"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Due date</Label>
                            <Input type="date" className="mt-1" value={form.due_date} onChange={(e) => s("due_date", e.target.value)} data-testid="nt-due" />
                        </div>
                        <div>
                            <Label>Priority</Label>
                            <Select value={form.priority} onValueChange={(v) => s("priority", v)}>
                                <SelectTrigger className="mt-1" data-testid="nt-priority"><SelectValue /></SelectTrigger>
                                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} data-testid="nt-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="nt-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function WelcomeEmailDialog({ task, onClose, onSent }) {
    const [markDone, setMarkDone] = useState(true);
    if (!task) return null;
    return (
        <EmailComposer
            open={!!task}
            onClose={onClose}
            onSent={() => onSent?.()}
            title="Send welcome email"
            kind={null}
            showTemplatePicker={false}
            toEditable={false}
            loadPreview={async () => {
                const { data } = await api.get(`/enrol/${task.enrolment_id}/welcome-email/preview`);
                return { to: data.to, to_name: data.to_name, subject: data.subject, html: data.html };
            }}
            onSend={async ({ subject, html }) => {
                const { data } = await api.post(`/enrol/${task.enrolment_id}/welcome-email`, {
                    subject, html, mark_task_done: markDone,
                });
                return data;
            }}
            extraFooter={(
                <label className="flex items-center gap-2 text-[12px] text-[#5E636E]" data-testid="welcome-email-mark-done-wrap">
                    <input
                        type="checkbox"
                        checked={markDone}
                        onChange={(e) => setMarkDone(e.target.checked)}
                        data-testid="welcome-email-mark-done"
                    />
                    Mark this welcome task as done after sending
                </label>
            )}
        />
    );
}
